"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 159: Bandai FCG / LZ93D50 with X24C01 EEPROM
 * ------------------------------------------------------------------ *
 * Le LZ93D50 equipe ici une EEPROM Xicor X24C01 de 128 octets au lieu
 * du 24C02 de 256 octets du mapper 16. Tous les registres de banques,
 * de miroir et d'IRQ sont ceux du LZ93D50 (submapper 5), donc le meme
 * ASIC que le mapper 16, mais uniquement dans $8000-$FFFF.
 *
 * La difference qui compte pour les jeux est le protocole serie :
 * Bandai croyait que le X24C01 etait little-endian et a code ses
 * sauvegardes ainsi. Les adresses de 7 bits et les octets de donnees
 * arrivent donc bit 0 en premier, et la lecture sort bit 0 en premier.
 * C'est exactement le modele de Mesen, et c'est ce que ces jeux
 * attendent, meme si la puce reelle est big-endian.
 *
 * Reference: https://www.nesdev.org/wiki/INES_Mapper_159
 * ------------------------------------------------------------------ */

// X24C01: 128 octets, adresse de mot sur 7 bits.
function Eeprom24C01() {
  this.data = new Uint8Array(128);
  this.onWrite = null;
  this.resetBus();
}

Eeprom24C01.prototype.resetBus = function () {
  this.mode = "idle";
  this.nextMode = "idle";
  this.bitCount = 0;
  this.address = 0;
  this.dataByte = 0;
  this.output = 1;
  this.prevScl = 1;
  this.prevSda = 1;
  this.reading = false;
};

Eeprom24C01.prototype.read = function () {
  return this.output;
};

Eeprom24C01.prototype.start = function () {
  this.mode = "address";
  this.nextMode = "idle";
  this.bitCount = 0;
  this.address = 0;
  this.dataByte = 0;
  this.output = 1;
  this.reading = false;
};

Eeprom24C01.prototype.stop = function () {
  this.mode = "idle";
  this.output = 1;
};

// Front montant de SCL. Les sept premiers bits forment l'adresse de mot,
// bit 0 en premier; le huitieme choisit lecture ou ecriture.
Eeprom24C01.prototype.clockRise = function (sda) {
  switch (this.mode) {
    case "address":
      if (this.bitCount < 7) {
        this.address |= sda << this.bitCount;
        this.bitCount++;
      } else if (this.bitCount === 7) {
        this.bitCount = 8;
        this.reading = sda === 1;
        this.nextMode = this.reading ? "read" : "write";
        if (this.reading) {
          this.dataByte = this.data[this.address & 0x7f];
        }
      }
      break;

    case "ack":
      // Neuvieme coup: l'EEPROM acquitte en tirant SDA a 0.
      this.output = 0;
      break;

    case "read":
      if (this.bitCount < 8) {
        this.bitCount++;
      }
      break;

    case "readAck":
      // ACK du maitre = 0: il veut l'octet suivant. NACK = 1: fin.
      if (sda === 0) {
        this.dataByte = this.data[this.address & 0x7f];
        this.mode = "read";
        this.bitCount = 0;
        this.output = this.dataByte & 1;
      } else {
        this.mode = "idle";
        this.output = 1;
      }
      break;

    case "write":
      if (this.bitCount < 8) {
        this.dataByte |= sda << this.bitCount;
        this.bitCount++;
      }
      break;
  }
};

// Front descendant de SCL: l'octet courant est complet, on passe a
// l'acquittement ou a l'octet suivant. En lecture, la donnee change
// pendant que SCL est bas, comme sur l'I2C.
Eeprom24C01.prototype.clockFall = function () {
  switch (this.mode) {
    case "address":
      if (this.bitCount === 8) {
        this.mode = "ack";
        this.output = 0;
        this.bitCount = 0;
      }
      break;

    case "ack":
      this.mode = this.nextMode;
      this.bitCount = 0;
      this.output = 1;
      if (this.mode === "read") {
        this.output = this.dataByte & 1;
      }
      break;

    case "write":
      if (this.bitCount === 8) {
        this.data[this.address & 0x7f] = this.dataByte;
        if (this.onWrite !== null) {
          this.onWrite(this.address & 0x7f, this.dataByte);
        }
        // Le X24C01 n'a pas de page: l'adresse reboucle sur 128 octets.
        this.address = (this.address + 1) & 0x7f;
        this.dataByte = 0;
        this.mode = "ack";
        this.nextMode = "write";
        this.output = 0;
        this.bitCount = 0;
      }
      break;

    case "read":
      if (this.bitCount === 8) {
        this.mode = "readAck";
        this.output = 1;
        this.bitCount = 0;
        this.address = (this.address + 1) & 0x7f;
      } else {
        this.output = (this.dataByte >> this.bitCount) & 1;
      }
      break;
  }
};

Eeprom24C01.prototype.write = function (scl, sda) {
  scl = scl ? 1 : 0;
  sda = sda ? 1 : 0;

  var prevScl = this.prevScl;
  var prevSda = this.prevSda;

  if (prevScl === 1 && scl === 1 && prevSda === 1 && sda === 0) {
    this.start();
  } else if (prevScl === 1 && scl === 1 && prevSda === 0 && sda === 1) {
    this.stop();
  } else if (prevScl === 0 && scl === 1) {
    this.clockRise(sda);
  } else if (prevScl === 1 && scl === 0) {
    this.clockFall();
  }

  this.prevScl = scl;
  this.prevSda = sda;
};

Eeprom24C01.prototype.toJSON = function () {
  return {
    mode: this.mode,
    nextMode: this.nextMode,
    bitCount: this.bitCount,
    address: this.address,
    dataByte: this.dataByte,
    output: this.output,
    prevScl: this.prevScl,
    prevSda: this.prevSda,
    reading: this.reading,
  };
};

Eeprom24C01.prototype.fromJSON = function (s) {
  if (!s) {
    this.resetBus();
    return;
  }
  this.mode = s.mode || "idle";
  this.nextMode = s.nextMode || "idle";
  this.bitCount = s.bitCount | 0;
  this.address = s.address | 0;
  this.dataByte = s.dataByte | 0;
  this.output = s.output === undefined ? 1 : s.output & 1;
  this.prevScl = s.prevScl === undefined ? 1 : s.prevScl & 1;
  this.prevSda = s.prevSda === undefined ? 1 : s.prevSda & 1;
  this.reading = s.reading === true;
};

function register(Mappers) {
  Mappers[159] = function (nes) {
    // The LZ93D50 banking, mirroring and IRQ logic is shared with mapper 16.
    Mappers[16].call(this, nes);

    var self = this;
    this.eeprom = new Eeprom24C01();
    this.eeprom.onWrite = function (address, value) {
      self.saveEepromByte(address, value);
    };
  };

  Mappers[159].prototype = new Mappers[16]();

  // Mapper 159 is the LZ93D50 variant only: unlike mapper 16 submapper 0 it
  // does not decode the FCG-1/2 register window at $6000-$7FFF. Writes there
  // are ignored, while reads still expose the EEPROM output on bit 4.
  Mappers[159].prototype.write = function (address, value) {
    if (address >= 0x6000 && address < 0x8000) {
      return;
    }

    Mappers[16].prototype.write.apply(this, arguments);
  };

  Mappers[159].prototype.loadBatteryRam = function () {
    // batteryRamData is sized for an 8 KB WRAM; only the first 128 bytes are
    // the X24C01 here, and $6000-$7FFF is not RAM at all.
    var ram = this.nes.rom.batteryRamData;
    if (ram !== null && ram !== undefined) {
      var count = Math.min(128, ram.length);
      for (var i = 0; i < count; i++) {
        this.eeprom.data[i] = ram[i];
      }
    }
  };
}

module.exports = { register: register };
